// src/pages/VideoEditorsAdmin.tsx
import LoginModal from "@/components/modals/LoginModal";
import { SessionContext } from "@/context/SessionContext";
import ConductionAPI from "@/javascript/conduction-api";
import { User } from "@/javascript/user-objects";
import { Utils } from "@/javascript/utils";
import {
    Box,
    Button,
    Card,
    Group,
    Input,
    Loader,
    ScrollArea,
    Skeleton,
    Table,
    Text,
    Title,
    Tooltip,
    useMantineTheme,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconAlertCircle, IconLock, IconRefresh, IconUserSearch } from "@tabler/icons-react";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

const HEADER_FW = 700;

const VideoEditorsAdmin: React.FC = () => {
    const theme = useMantineTheme();
    const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
    const fzMed = isMobile ? 12 : 14;

    const session = useContext(SessionContext);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [userHasAccess, setUserHasAccess] = useState(false);

    // data
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [editors, setEditors] = useState<User[] | undefined>(undefined);

    // ui
    const [query, setQuery] = useState("");

    if (!session) return null;

    /* -------------------------------- access gate -------------------------------- */

    useEffect(() => {
        const u = session.user;
        if (u?.roles?.includes("ConductionAdmin") || u?.roles?.includes("InstrumentalistLiaison")) {
            setUserHasAccess(true);
            setShowLoginModal(false);
        } else {
            setUserHasAccess(false);
            if (!u) setShowLoginModal(true);
        }
    }, [session.user]);

    /* ---------------------------------- load all --------------------------------- */

    const loadEditors = useCallback(async () => {
        if (!session.token) return;
        try {
            setLoading(true);
            setEditors(undefined);

            // Prefer a dedicated API if available, otherwise fall back to all users and filter by role.
            let list: User[] | undefined;
            try {
                const maybe = await (ConductionAPI as any).getAllEditors?.(session.token);
                if (Array.isArray(maybe)) list = maybe as User[];
            } catch {
                // ignore and fall back
            }
            if (!list) {
                const allUsers: User[] = (await (ConductionAPI as any).getAllUsers?.(session.token)) ?? [];
                list = allUsers.filter(u => u.isVideoEditor);
            }

            setEditors(list ?? []);
        } catch (err) {
            console.error("Error loading editors:", err);
            Utils.displayNotification(
                "Load Failure",
                "Unable to load video editors. Please try again.",
                "red",
                <IconAlertCircle size={16} />
            );
            setEditors([]);
        } finally {
            setLoading(false);
        }
    }, [session.token]);

    useEffect(() => {
        void loadEditors();
    }, [loadEditors]);

    const handleRefresh = useCallback(async () => {
        setRefreshing(true);
        await loadEditors();
        setRefreshing(false);
    }, [loadEditors]);

    /* --------------------------------- searching -------------------------------- */

    const queryLower = query.trim().toLowerCase();

    const filtered = useMemo(() => {
        if (!editors) return [];
        if (!queryLower) return editors;
        return editors.filter((u) => {
            const full = `${u.firstName ?? ""} ${u.lastName ?? ""}`.toLowerCase();
            const username = (u.username ?? "").toLowerCase();
            const email = (u.email ?? "").toLowerCase();
            return full.includes(queryLower) || username.includes(queryLower) || email.includes(queryLower);
        });
    }, [editors, queryLower]);

    /* ----------------------------------- view ----------------------------------- */

    const skeletonRow = (
        <Table.Tr>
            {Array.from({ length: isMobile ? 1 : 2 }).map((_, i) => (
                <Table.Td key={i}>
                    <Skeleton height={18} />
                </Table.Td>
            ))}
        </Table.Tr>
    );

    return !userHasAccess ? (
        <>
            <LoginModal showModal={showLoginModal} setShowModal={setShowLoginModal} />
            <Card p={20}>
                <IconLock size={48} color={theme.colors.red[6]} />
                <Text>You do not have permission to access this page.</Text>
                <Text>
                    Please log into an account with access or <Link to="/">click here to navigate away</Link>.
                </Text>
            </Card>
        </>
    ) : (
        <Box
            w="100%"
            h="100%"
            p="xs"
            style={{
                display: "grid",
                gridTemplateRows: "auto 1fr",
                overflow: "hidden",
                minHeight: 0,
            }}
        >
            {/* Header */}
            <Title p="xs" size={30} c={theme.colors.blue[0]} ta="center">
                Video Editors
            </Title>

            {/* Content */}
            <Card
                w="98%"
                bg={theme.colors.blue[8]}
                withBorder={false}
                radius="lg"
                p={isMobile ? "sm" : "xl"}
                style={{
                    justifySelf: "center",
                    alignSelf: "stretch",
                    display: "grid",
                    gridTemplateRows: "auto 1fr",
                    overflow: "hidden",
                    minHeight: 0,
                }}
            >
                {/* Toolbar */}
                <Group justify="space-between" align="center" mb="md" wrap="wrap">
                    <Input
                        leftSection={<IconUserSearch size={16} />}
                        placeholder="Search by name, username, email..."
                        value={query}
                        onChange={(e) => setQuery(e.currentTarget.value)}
                        w={isMobile ? 260 : 380}
                        styles={{ input: { background: theme.colors.blue[0] } }}
                    />
                    <Button
                        leftSection={refreshing ? <Loader size="xs" /> : <IconRefresh size={16} />}
                        onClick={handleRefresh}
                        variant="default"
                        c={theme.colors.blue[8]}
                        disabled={loading}
                    >
                        Refresh
                    </Button>
                </Group>

                {/* Scroll window */}
                <Box
                    bg={theme.colors.blue[0]}
                    style={{
                        borderRadius: 16,
                        overflow: "hidden",
                        minHeight: 0,
                        display: "grid",
                        gridTemplateRows: "1fr",
                    }}
                >
                    <ScrollArea
                        h="100%"
                        type="auto"
                        viewportProps={{ style: { height: "100%" } }}
                        styles={{ viewport: { overflowX: "hidden" } }}
                    >
                        <Table
                            striped
                            highlightOnHover
                            stickyHeader
                            withTableBorder={false}
                            withColumnBorders={false}
                            verticalSpacing="sm"
                            horizontalSpacing="md"
                            style={{ tableLayout: "auto" }}
                        >
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th w={isMobile ? "260px" : "320px"}>
                                        <Text fw={HEADER_FW} fz={fzMed}>Editor</Text>
                                    </Table.Th>
                                    {!isMobile && (
                                        <Table.Th w="360px">
                                            <Text fw={HEADER_FW} fz={fzMed}>Email</Text>
                                        </Table.Th>
                                    )}
                                </Table.Tr>
                            </Table.Thead>

                            <Table.Tbody>
                                {loading && (
                                    <>
                                        {React.cloneElement(skeletonRow, { key: "s1" })}
                                        {React.cloneElement(skeletonRow, { key: "s2" })}
                                        {React.cloneElement(skeletonRow, { key: "s3" })}
                                    </>
                                )}

                                {!loading && filtered.length === 0 && (
                                    <Table.Tr>
                                        <Table.Td colSpan={isMobile ? 1 : 2}>
                                            <Text ta="center" fw={500}>
                                                No editors found.
                                            </Text>
                                        </Table.Td>
                                    </Table.Tr>
                                )}

                                {!loading &&
                                    filtered.map((u) => (
                                        <Table.Tr key={u.id}>
                                            <Table.Td>
                                                <Group gap={6}>
                                                    <Text fw={600}>{u.username}</Text>
                                                    <Text fz="sm" c="dimmed">
                                                        {[u.firstName, u.lastName].filter(Boolean).join(" ") || "—"}
                                                    </Text>
                                                </Group>
                                            </Table.Td>
                                            {!isMobile && (
                                                <Table.Td>
                                                    <Group gap="xs">
                                                        <Text fz="sm">{u.email || "—"}</Text>
                                                        {!u.emailConfirmed && (
                                                            <Tooltip label="Email not verified">
                                                                <IconAlertCircle size={16} color="orange" />
                                                            </Tooltip>
                                                        )}
                                                    </Group>
                                                </Table.Td>
                                            )}
                                        </Table.Tr>
                                    ))}
                            </Table.Tbody>
                        </Table>
                    </ScrollArea>
                </Box>
            </Card>
        </Box>
    );
};

export default VideoEditorsAdmin;
